import { problemaDeContrasena } from "@printflow/auth-shared";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { CLASE_BOTON_AUTH, MarcoAuth } from "../../components/auth/MarcoAuth";
import { Aviso, Campo } from "../../components/ui/primitivos";
import { authClient } from "../../lib/auth-client";

/**
 * Restablecer la contraseña con el token del mail (ADR 0104 §5). Pública.
 *
 * La regla de complejidad es la del servidor (`problemaDeContrasena`, de
 * `auth-shared`); la confirmación es solo de cliente, como en `imprenta`.
 */
const VUELTA_AL_LOGIN_MS = 3000;
const ERROR_GENERICO = "No pudimos actualizar la contraseña. Probá de nuevo.";

function EnlaceInvalido() {
  return (
    <MarcoAuth titulo="Enlace inválido">
      <p className="text-sm text-neutral-600">
        El enlace para restablecer la contraseña es inválido o ha expirado.
      </p>
      <Link to="/olvide-contrasena" className={CLASE_BOTON_AUTH}>
        Solicitar nuevo enlace
      </Link>
      <Link to="/" className="block text-center text-sm text-neutral-600 underline">
        Volver a iniciar sesión
      </Link>
    </MarcoAuth>
  );
}

export function RestablecerContrasenaPagina() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navegar = useNavigate();

  const [nueva, setNueva] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [actualizando, setActualizando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalido, setInvalido] = useState(false);
  const [listo, setListo] = useState(false);

  useEffect(() => {
    if (!listo) return;
    const t = setTimeout(() => navegar("/"), VUELTA_AL_LOGIN_MS);
    return () => clearTimeout(t);
  }, [listo, navegar]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const problema = problemaDeContrasena(nueva);
    if (problema) return setError(problema);
    if (nueva !== confirmar) return setError("Las contraseñas no coinciden");

    setError(null);
    setActualizando(true);
    try {
      const res = await authClient.resetPassword({ newPassword: nueva, token });
      if (!res.error) setListo(true);
      else if (res.error.code === "INVALID_TOKEN") setInvalido(true);
      else setError(res.error.message || ERROR_GENERICO);
    } catch {
      setError(ERROR_GENERICO);
    }
    setActualizando(false);
  }

  if (!token || invalido) return <EnlaceInvalido />;

  if (listo) {
    return (
      <MarcoAuth titulo="Contraseña actualizada">
        <Aviso tono="ok">Tu contraseña se actualizó. Te llevamos al inicio de sesión.</Aviso>
        <Link to="/" className="block text-center text-sm text-neutral-600 underline">
          Ir a iniciar sesión
        </Link>
      </MarcoAuth>
    );
  }

  return (
    <MarcoAuth
      titulo="Nueva contraseña"
      subtitulo="Ingresá tu nueva contraseña. Tiene que llevar 8 o más caracteres, mayúscula, minúscula, número y carácter especial."
    >
      <form onSubmit={onSubmit} className="space-y-5">
        <Campo
          etiqueta="Nueva contraseña"
          type="password"
          required
          autoComplete="new-password"
          value={nueva}
          onChange={(e) => setNueva(e.target.value)}
        />
        <Campo
          etiqueta="Confirmar contraseña"
          type="password"
          required
          autoComplete="new-password"
          value={confirmar}
          onChange={(e) => setConfirmar(e.target.value)}
        />
        {error ? <Aviso>{error}</Aviso> : null}
        <button type="submit" className={CLASE_BOTON_AUTH} disabled={actualizando}>
          {actualizando ? "Actualizando..." : "Restablecer contraseña"}
        </button>
      </form>
    </MarcoAuth>
  );
}
